import { useEffect, useState } from "react";
import axios from "axios";
import { PLANS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = [
  { slug: "term-life", name: "Term Life", tagline: "Affordable coverage for life's biggest decades.", horizon: "10–30 year terms", best_for: "Young families, mortgages, income replacement.", starting_at: "$15/mo" },
  { slug: "whole-life", name: "Whole Life", tagline: "Lifelong protection that builds cash value.", horizon: "Coverage for life", best_for: "Legacy planning and guaranteed growth.", starting_at: "$45/mo" },
  { slug: "iul", name: "Indexed Universal Life", tagline: "Permanent coverage with market-linked growth.", horizon: "Flexible, lifelong", best_for: "Tax-advantaged accumulation, retirement supplement.", starting_at: "$85/mo" },
  { slug: "final-expense", name: "Final Expense", tagline: "Dignity for your loved ones when it matters.", horizon: "Lifetime", best_for: "Seniors 50–85, simplified underwriting.", starting_at: "$22/mo" },
  { slug: "mortgage-protection", name: "Mortgage Protection", tagline: "Keep the family in the home, no matter what.", horizon: "Matches your mortgage term", best_for: "Homeowners with dependents.", starting_at: "$28/mo" },
];

export default function Plans() {
  const [plans, setPlans] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/plans`).then((res) => {
      if (alive && res.data?.plans?.length) setPlans(res.data.plans);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onSelect = (slug) => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    // pre-fill plan via global event
    window.dispatchEvent(new CustomEvent("prefill-plan", { detail: slug }));
  };

  return (
    <section id="plans" data-testid={PLANS.section} className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-4">Coverage for every chapter</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-primary leading-[1.05]">
              Five families.<br />
              <em className="not-italic text-accent">One trusted broker.</em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Whether you&rsquo;re 28 with a new mortgage or 68 protecting a final wish,
              we&rsquo;ll match you to the right product from 25+ A-rated carriers. No fees, no obligation.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((p, idx) => (
            <article
              key={p.slug}
              data-testid={PLANS.card(p.slug)}
              className={`relative rounded-[2rem] p-8 border border-border/60 bg-card soft-shadow hover-lift ${idx === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.horizon}</p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-primary mt-2 leading-tight">{p.name}</h3>
                </div>
              </div>
              <p className="font-serif italic text-xl text-foreground/80 leading-snug mb-4">{p.tagline}</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">{p.best_for}</p>
              <Button
                data-testid={PLANS.selectBtn(p.slug)}
                onClick={() => onSelect(p.slug)}
                variant="ghost"
                className="rounded-full pl-0 hover:bg-transparent text-primary group"
              >
                Get this quote
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
